import React from "react";

/**
 * Matches `{variable}` markup emitted by the assistant in script drafts —
 * exactly one pair of curly braces around a variable name, e.g.
 * "Hi {first_name}". Lookarounds reject double-brace `{{template}}` markup
 * (used elsewhere for workflow templates) so it isn't misread as a variable.
 *
 * Distinct from `VARIABLE_REGEX` in `scriptPreviewUtils` (the `@[Label](id)`
 * mention format used by saved scripts): drafts are not yet bound to real
 * contact variables, so the assistant uses plain placeholder names.
 */
export const DRAFT_VARIABLE_REGEX = /(?<!\{)\{([a-zA-Z_][a-zA-Z0-9_]*)\}(?!\})/g;

/** Humanize a snake_case variable name for display, e.g. "first_name" → "first name". */
const humanizeVariableName = (name: string): string => name.replaceAll("_", " ");

/**
 * Split draft text on `{variable}` tokens and render each token as a
 * highlighted pill, mirroring the variable styling used on the script
 * preview page. Always returns an array of renderable parts.
 */
export function renderDraftContentWithVariables(
   text: string
): (string | React.JSX.Element)[] {
   const regex = new RegExp(DRAFT_VARIABLE_REGEX.source, DRAFT_VARIABLE_REGEX.flags);
   const parts: (string | React.JSX.Element)[] = [];
   let lastIndex = 0;
   let match;

   while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
         parts.push(text.slice(lastIndex, match.index));
      }
      parts.push(
         <span
            key={match.index}
            className="inline-block px-1.5 py-0.5 rounded bg-primary/20 text-primary text-xs font-medium"
         >
            {humanizeVariableName(match[1])}
         </span>
      );
      lastIndex = match.index + match[0].length;
   }

   if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
   }

   return parts;
}
